import { Retriever } from "../Retriever";
import * as i from "../contracts";
import { computed, IObjectDidChange } from "mobx";
import { observable, Lambda, observe, action } from "mobx";
import { mapHasLiveEvents, mapHasLiveStreaming, mapLeagueUrl, sortLeagues, filterLeagues, mapSelected, mapRequiredProps } from "../composable";
import { ChannelListeners, CMSConfig } from "../Typings";
import { InternalMessageBusChannels, internalMessageBus, InternalMessageBusMessage, UserInfoCurrent } from "sb-resp-lib";
import Debounce from "debounce-decorator";

export class Parser {
    private retriever: Retriever;
    private config: CMSConfig;
    private globalListeners!: ChannelListeners;
    public sportId: string;
    private regionIds: string[];

    @observable leagues: i.League[];
    @observable selectedLeagues: string[];
    @observable isLoading: boolean;
    leaguesDisposer: Lambda;
    navigationHandler: any;

    constructor(retriever: Retriever, config: CMSConfig, navigationHandler: any) {
        this.retriever = retriever;
        this.leagues = [];
        this.selectedLeagues = [];
        this.navigationHandler = navigationHandler;
        this.config = config;
        this.isLoading = false;
        //this.mapLeagueLinkUrl = mapLeagueUrl.bind(this, navigationHandler.Navigation.adjustPath);
        this.onLogin = this.onLogin.bind(this);
        this.globalListeners = {};
        this.globalListeners[InternalMessageBusChannels.legacy.platformJWTChange.renewJWTToken] = "renewJWTToken";
        this.globalListeners[InternalMessageBusChannels.legacy.userInfo.onLogin] = "onLogin";
        this.globalListeners[InternalMessageBusChannels.legacy.userInfo.onLogout] = "onLogout";
        this.leaguesDisposer = () => {};
        this.sportId = "1";
        this.regionIds = [config.regionId];
    }

    private attachMsgBusRouter() {
        Object.keys(this.globalListeners).forEach(x => internalMessageBus.on(x, this.msgRouterGlobal, this));
    }

    private detachMsgBusRouter() {
        Object.keys(this.globalListeners).forEach(x => internalMessageBus.off(x, this.msgRouterGlobal, this));
    }

    /* istanbul ignore next */
    private msgRouterGlobal(msg: InternalMessageBusMessage<any>) {
        this[this.globalListeners[`${msg.channel}.${msg.subChannel.join(".")}`]](...msg.data);
    }

    private requestGlobalListeners() {
        internalMessageBus.emit(InternalMessageBusChannels.legacy.userInfo.request, { callback: this.onLogin });
    }

    /* istanbul ignore next */
    private attachObservables() {
        this.leaguesDisposer = observe(this.retriever, (change: IObjectDidChange) => {
            this.updateLeagues();
        });
    }

    public onLogin(user: UserInfoCurrent) {
        if (user) {
            this.retriever.setRegionId(user.CountryOfRegistration);
            this.regionIds = [this.config.regionId, user.CountryOfRegistration];
        }
    }

    public onLogout() {
        this.retriever.removeRegionId();
        this.regionIds = [this.config.regionId];
    }

    /* istanbul ignore next */
    @Debounce 100
    public renewJWTToken() {
        /* istanbul ignore next */
        this.retriever.leaguesRetriever && this.retriever.leaguesRetriever.loadData();
    }
    /* istanbul ignore next */
    private detachObservables() {
        this.leaguesDisposer();
    }

    activate() {
        this.retriever.activate();
        this.attachObservables();
        this.attachMsgBusRouter();
        this.requestGlobalListeners();
    }

    selectRetriever(sportId: string, leagueIds?: string[]) {
        this.clearData();
        this.sportId = sportId;
        this.retriever.selectRetriever(sportId, leagueIds);
    }

    deactivate() {
        this.detachObservables();
        this.detachMsgBusRouter();
        this.clearData();
        this.retriever.deactivate();
    }

    /* istanbul ignore next */
    @action.bound private updateLeagues() {
        let leagues: i.League[] = this.retriever.leagues.slice();

        leagues = filterLeagues(leagues, this.sportId);
        leagues = sortLeagues(leagues, this.config, this.sportId);
        leagues = mapSelected(leagues, this.selectedLeagues);

        this.leagues = leagues.map(league => {
            mapRequiredProps(league);
            mapHasLiveEvents(league);
            mapHasLiveStreaming(league);
            mapLeagueUrl(this.navigationHandler.Navigation.adjustPath, league);
            return league;
        });
    }
    /* istanbul ignore next */
    @action.bound clearData() {
        this.leagues = [];
        this.selectedLeagues = [];
    }

    /* istanbul ignore next */
    toggleSelectedLeague(leagueId: string) {
        if (this.selectedLeagues.length >= this.config.maxCouponLeagues && this.selectedLeagues.indexOf(leagueId) === -1) {
            return false;
        } else {
            this.updateSelectedLeagues(leagueId);
            this.toggleIsSelectedLeagues(leagueId);
            return true;
        }
    }
    /* istanbul ignore next */
    @action updateSelectedLeagues(leagueId: string) {
        let selLeagues = this.selectedLeagues.slice();

        const selectedIndex = selLeagues.indexOf(leagueId);
        selectedIndex > -1 ? selLeagues.splice(selectedIndex, 1) : selLeagues.push(leagueId);

        this.selectedLeagues = selLeagues;
    }
    /* istanbul ignore next */
    @action toggleIsSelectedLeagues(leagueId: string) {
        let leagues: i.League[] = this.leagues.slice();

        const leagueIndex: number = this.leagues.findIndex(league => league.id === leagueId);
        if (leagueIndex > -1) {
            leagues[leagueIndex].isSelected = !leagues[leagueIndex].isSelected;
        }

        this.leagues = leagues;
    }
    /* istanbul ignore next */
    @computed get getTopLeaguesOnly(): i.League[] {
        /* istanbul ignore next */
        return this.leagues.filter(league => league.isTopLeague
            || this.regionIds.indexOf(league.regionId || "") >= 0);
    }
}